import { useCallback, useEffect, useRef, useState } from "react";

// Golomt B2B often opens cp.dehub.mn/login (OAuth) then the bank. The popup may
// post BANK_AUTH_DONE (same-origin /bank-callback) or DAN_AUTH_DONE (cp/auth).
// Payment confirmation still lands asynchronously on the invoice row, so we
// always poll until paid — auth messages only accelerate a tick / help close.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

type StartArgs = {
  popup: Window | null;
  checkPaid: () => Promise<boolean>;
  onPaid: () => void;
  onPending: () => void;
};

export function useFeePaymentPolling() {
  const [polling, setPolling] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);
  const activeRef = useRef(false);
  const busyRef = useRef(false);
  const deadlineRef = useRef(0);
  const tickRef = useRef<(() => void) | null>(null);
  const onPaidRef = useRef<(() => void) | null>(null);
  const onPendingRef = useRef<(() => void) | null>(null);
  const checkPaidRef = useRef<(() => Promise<boolean>) | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    activeRef.current = false;
    busyRef.current = false;
    tickRef.current = null;
    setPolling(false);
  }, []);

  const closePopup = useCallback(() => {
    try {
      popupRef.current?.close();
    } catch {
      // cross-origin popup — close may throw, ignore
    }
    popupRef.current = null;
  }, []);

  const start = useCallback(
    ({ popup, checkPaid, onPaid, onPending }: StartArgs) => {
      if (activeRef.current) return;

      activeRef.current = true;
      popupRef.current = popup;
      deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
      checkPaidRef.current = checkPaid;
      onPaidRef.current = onPaid;
      onPendingRef.current = onPending;
      setPolling(true);

      const finishPaid = () => {
        stop();
        closePopup();
        onPaidRef.current?.();
      };

      const finishPending = () => {
        stop();
        closePopup();
        onPendingRef.current?.();
      };

      const tick = async () => {
        if (!activeRef.current || busyRef.current) return;
        busyRef.current = true;

        try {
          const popupClosed = !!(popupRef.current && popupRef.current.closed);
          const timedOut = Date.now() > deadlineRef.current;
          const finalAttempt = popupClosed || timedOut;

          try {
            if (checkPaidRef.current && (await checkPaidRef.current())) {
              finishPaid();
              return;
            }
          } catch {
            // Transient fetch error — keep polling unless this was the last try.
          }

          if (finalAttempt) finishPending();
        } finally {
          busyRef.current = false;
        }
      };

      tickRef.current = () => {
        void tick();
      };

      // First check immediately (do not wait for the first interval).
      void tick();

      timerRef.current = setInterval(() => {
        void tick();
      }, POLL_INTERVAL_MS);
    },
    [stop, closePopup],
  );

  // Mirror de-payment-admin openBankConsent: auth/callback messages close the
  // popup and force an immediate status check (payment may already be applied).
  useEffect(() => {
    if (!polling) return;

    const onMessage = (event: MessageEvent) => {
      const type = event.data?.type;
      if (type !== "BANK_AUTH_DONE" && type !== "DAN_AUTH_DONE") return;
      // BANK_AUTH_DONE is same-origin (/bank-callback). DAN_AUTH_DONE is posted
      // with "*" from cp/auth — accept any origin for that type only.
      if (type === "BANK_AUTH_DONE" && event.origin !== window.location.origin) return;

      closePopup();
      tickRef.current?.();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [polling, closePopup]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      activeRef.current = false;
      busyRef.current = false;
      try {
        popupRef.current?.close();
      } catch {
        /* ignore */
      }
    };
  }, []);

  return { polling, start, stop };
}
